import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { categories, mainSiteUrl } from "@/mocks/categories";

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-background-50/95 backdrop-blur-md border-b border-background-200/70">
      <div className="w-full px-4 md:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-18">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <span className="font-heading text-xl md:text-2xl font-bold text-foreground-950 tracking-tight">
              Mulebuy<span className="text-primary-500">Spreadsheets</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-5 lg:gap-7">
            <Link
              to="/"
              className="text-sm font-semibold text-foreground-900 hover:text-primary-600 transition-colors whitespace-nowrap"
            >
              Home
            </Link>

            {/* Category Dropdown */}
            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center gap-1.5 text-sm font-semibold text-foreground-900 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                Categories
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}></i>
              </button>
              {isOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-64 bg-background-50 rounded-xl border border-background-200 shadow-lg py-2 max-h-80 overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.slug}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="block px-4 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-100 hover:text-primary-600 transition-colors whitespace-nowrap"
                      onClick={() => setIsOpen(false)}
                    >
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/what-is-mulebuy-spreadsheet"
              className="text-sm font-semibold text-foreground-900 hover:text-primary-600 transition-colors whitespace-nowrap"
            >
              Shopping Guide
            </Link>

            {/* More Articles Dropdown */}
            <div className="relative group">
              <button className="flex items-center gap-1.5 text-sm font-semibold text-foreground-900 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer">
                Articles
                <i className="ri-arrow-down-s-line text-sm group-hover:rotate-180 transition-transform duration-200"></i>
              </button>
              <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-72 bg-background-50 rounded-xl border border-background-200 shadow-lg py-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                <Link to="/what-is-mulebuy-spreadsheet" className="block px-4 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-100 hover:text-primary-600 transition-colors">
                  Complete Guide
                </Link>
                <Link to="/how-to-use-mulebuy-spreadsheet" className="block px-4 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-100 hover:text-primary-600 transition-colors">
                  Beginner Tutorial
                </Link>
                <Link to="/mulebuy-spreadsheet-vs-alternatives" className="block px-4 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-100 hover:text-primary-600 transition-colors">
                  Platform Comparison
                </Link>
                <Link to="/is-mulebuy-spreadsheet-safe" className="block px-4 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-100 hover:text-primary-600 transition-colors">
                  Safety Guide
                </Link>
              </div>
            </div>
          </div>

          {/* CTA Button */}
          <div className="hidden md:flex items-center">
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 text-sm font-bold rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Visit Mulebuy
              <i className="ri-arrow-right-up-line"></i>
            </a>
          </div>

          {/* Mobile Hamburger */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-950 cursor-pointer"
          >
            <i className={`text-xl ${mobileMenuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 px-4 py-4 max-h-[80vh] overflow-y-auto">
          <div className="flex flex-col gap-2">
            <Link
              to="/"
              className="text-sm font-semibold text-foreground-900 py-2.5 px-2 rounded-lg hover:bg-background-100 transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              <i className="ri-home-4-line mr-2"></i>
              Home
            </Link>

            <Link
              to="/what-is-mulebuy-spreadsheet"
              className="text-sm font-semibold text-foreground-900 py-2.5 px-2 rounded-lg hover:bg-background-100 transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              <i className="ri-book-open-line mr-2"></i>
              Shopping Guide
            </Link>

            {/* Mobile Categories Accordion */}
            <div className="border-t border-background-200/70 pt-2 mt-1">
              <button
                onClick={() => setMobileCatOpen(!mobileCatOpen)}
                className="w-full flex items-center justify-between text-sm font-semibold text-foreground-900 py-2.5 px-2 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
              >
                <span>
                  <i className="ri-grid-line mr-2"></i>
                  Categories
                </span>
                <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileCatOpen ? "rotate-180" : ""}`}></i>
              </button>
              {mobileCatOpen && (
                <div className="pl-6 space-y-0.5 mt-1">
                  {categories.map((cat) => (
                    <a
                      key={cat.slug}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="block text-sm text-foreground-600 py-2 px-2 rounded-lg hover:bg-background-100 hover:text-primary-600 transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* Mobile Articles */}
            <div className="border-t border-background-200/70 pt-2 mt-1">
              <p className="text-xs font-bold text-foreground-400 uppercase tracking-wider px-2 mb-1">Articles</p>
              <Link to="/what-is-mulebuy-spreadsheet" className="block text-sm text-foreground-600 py-2 px-2 rounded-lg hover:bg-background-100 hover:text-primary-600 transition-colors" onClick={() => setMobileMenuOpen(false)}>
                Complete Guide
              </Link>
              <Link to="/how-to-use-mulebuy-spreadsheet" className="block text-sm text-foreground-600 py-2 px-2 rounded-lg hover:bg-background-100 hover:text-primary-600 transition-colors" onClick={() => setMobileMenuOpen(false)}>
                Beginner Tutorial
              </Link>
              <Link to="/mulebuy-spreadsheet-vs-alternatives" className="block text-sm text-foreground-600 py-2 px-2 rounded-lg hover:bg-background-100 hover:text-primary-600 transition-colors" onClick={() => setMobileMenuOpen(false)}>
                Platform Comparison
              </Link>
              <Link to="/is-mulebuy-spreadsheet-safe" className="block text-sm text-foreground-600 py-2 px-2 rounded-lg hover:bg-background-100 hover:text-primary-600 transition-colors" onClick={() => setMobileMenuOpen(false)}>
                Safety Guide
              </Link>
            </div>

            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-primary-500 text-background-50 text-sm font-bold rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer mt-3"
            >
              Visit Mulebuy
              <i className="ri-arrow-right-up-line"></i>
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}